import { useState, type MouseEvent } from 'react'
import { Box, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useReleaseChannel } from '@/features/auth'
import type { SessionType } from '@/shared/types/yakurekiCore'
import { RADIUS } from '@/shared/styles/radii'

// 録音の文脈 pill。1 つの pill に 2 軸を畳む (= 独立 pill を増やさない):
//   来局/在宅 (session_type) … 提出書類 (在宅=訪問報告書 / 外来=情報提供書) を分ける軸
//   本人のみ/同伴・代理あり (has_companion) … 服薬指導の相手
// dropdown は 2 グループの radio。trigger は非既定 (在宅) だけ前置きして surface する
// (= 既定の来局は出さず、在宅のときだけ「在宅・本人のみ」のように前に付ける)。
const COMPANION_LABEL = {
  false: '本人のみ',
  true: '同伴・代理あり',
} as const

interface Props {
  // 来局/在宅。省略時は 来局/在宅 グループを出さない (companion のみの pill として振る舞う)。
  sessionType?: SessionType
  onSessionTypeChange?: (sessionType: SessionType) => void
  // 服薬指導の相手。省略時は本グループを出さない (来局/在宅 のみの pill として振る舞う)。
  // 新規録音 (フリー録音) は開始時点で event が無く、同伴有無を送る先が無いので省略する。
  hasCompanion?: boolean
  onCompanionChange?: (hasCompanion: boolean) => void
  disabled?: boolean
}

export const RecordingContextMenu = ({
  sessionType,
  onSessionTypeChange,
  hasCompanion,
  onCompanionChange,
  disabled = false,
}: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const open = Boolean(anchor)
  // 来局/在宅 の選択は β の channel でだけ出す (ga は walk_in 固定)。α は左ナビ「在宅」の
  // 患者のページから在宅の録音を作るので、ここには出さない (yakureki-back log/267)。
  const { isBeta, isAlpha } = useReleaseChannel()
  const showSessionGroup =
    isBeta && !isAlpha && sessionType !== undefined && !!onSessionTypeChange
  const showCompanionGroup = hasCompanion !== undefined && !!onCompanionChange
  const isHomeVisit = sessionType === 'home_visit'

  // どのグループも出せないなら pill ごと出さない (開いても空のメニューになるため)。
  // session 軸のみ渡す新規録音 page を非 β ユーザーが開いた場合がこの経路。
  if (!showSessionGroup && !showCompanionGroup) return null

  const companionLabel = COMPANION_LABEL[hasCompanion ? 'true' : 'false']
  // 在宅は出力が変わる重要な軸なので trigger に surface する。来局 (既定) は出さない。
  // companion グループが無い pill では在宅/来局 だけを出す。
  const triggerLabel = showCompanionGroup
    ? isHomeVisit
      ? `在宅・${companionLabel}`
      : companionLabel
    : isHomeVisit
    ? '在宅'
    : '来局'

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) =>
    setAnchor(e.currentTarget)
  const close = () => setAnchor(null)

  const trigger = (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={handleOpen}
      aria-haspopup="menu"
      aria-label="録音の文脈"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        height: 32,
        px: 1.25,
        border: 'none',
        borderRadius: `${RADIUS.full}px`,
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 500,
        color: open ? colorTokens.fg.default : colorTokens.fg.muted,
        bgcolor: open ? colorTokens.overlay.hover : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 120ms, color 120ms',
        '&:hover': {
          bgcolor: disabled ? 'transparent' : colorTokens.overlay.hover,
          color: colorTokens.fg.default,
        },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          mr: 0.5,
          '& svg': { fontSize: 16, display: 'block' },
        }}
      >
        {/* 軸アイコンは常に人 (文脈=服薬指導の相手) で固定. 来局/在宅はラベル側で示す. */}
        <GroupOutlinedIcon />
      </Box>
      {triggerLabel}
      <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
    </Box>
  )

  // グループ見出し (淡い小見出し。正典 §3.2 label/micro)
  const groupHeading = (text: string) => (
    <Typography
      sx={{
        px: 1,
        pt: 1,
        pb: 0.25,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.04em',
        color: colorTokens.fg.faint,
      }}
    >
      {text}
    </Typography>
  )

  const optionItem = (
    label: string,
    selected: boolean,
    onSelect: () => void
  ) => (
    <MenuItem
      key={label}
      disableRipple
      onClick={() => {
        onSelect()
        close()
      }}
      sx={{
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.75,
        borderRadius: `${RADIUS.md}px`,
        bgcolor: 'transparent',
        '&:hover': { bgcolor: colorTokens.overlay.hover },
      }}
    >
      <Box sx={{ width: 18, flexShrink: 0, display: 'inline-flex' }}>
        {selected && (
          <CheckIcon sx={{ fontSize: 16, color: colorTokens.fg.default }} />
        )}
      </Box>
      <Typography
        sx={{ fontSize: 14, fontWeight: 500, color: colorTokens.fg.default }}
      >
        {label}
      </Typography>
    </MenuItem>
  )

  return (
    <>
      {disabled ? (
        trigger
      ) : (
        <Tooltip title="録音の文脈" placement="bottom">
          {trigger}
        </Tooltip>
      )}
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              minWidth: 220,
              borderRadius: `${RADIUS.lg}px`,
              p: 0.5,
              border: `1px solid ${colorTokens.border.subtle}`,
              boxShadow: colorTokens.shadow.popover,
            },
          },
          list: { sx: { py: 0 } },
        }}
      >
        {showSessionGroup && groupHeading('来局 / 在宅')}
        {showSessionGroup &&
          optionItem('来局', sessionType === 'walk_in', () =>
            onSessionTypeChange!('walk_in')
          )}
        {showSessionGroup &&
          optionItem('在宅', sessionType === 'home_visit', () =>
            onSessionTypeChange!('home_visit')
          )}
        {showCompanionGroup && groupHeading('服薬指導の相手')}
        {showCompanionGroup &&
          optionItem('本人のみ', !hasCompanion, () =>
            onCompanionChange!(false)
          )}
        {showCompanionGroup &&
          optionItem('同伴・代理あり', !!hasCompanion, () =>
            onCompanionChange!(true)
          )}
      </Menu>
    </>
  )
}
